import * as CSV from 'csv-string'
import {array, Decoder, field, object} from 'json-bouncer'
import * as R from 'ramda'
import {url} from '../../App/AuthenticatedRoute'
import {indexedFilter, indexedMap} from '../../Data/ArrayHelper'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import {
    arrayHasUniqueStringKeyValidator,
    mandatoryNullableValidator,
    mandatoryStringValidator,
    stringToFixedPointNumberValidator,
    stringToIntValidator,
    validateInputField,
    validateRows
} from '../../Data/FormValidation'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {HttpRequest} from '../../Data/HttpRequest'
import {update} from '../../Data/ObjectHelper'
import * as Result from '../../Data/Result'
import {navigate} from '../../Data/Browser'
import {Session} from '../../Data/Session'
import {Customer} from '../../Entities/Customer'
import {
    MessageWithTranslation,
    messageWithTranslationDecoder
} from '../../Entities/MessageWithTranslation.entity.evolve'
import {
    ProductCalculationImportBaseData,
    productCalculationImportBaseDataDecoder
} from '../../Entities/ProductCalculationImportBaseData'
import {ProductShippingService} from '../../Entities/ProductShippingService'
import * as ProductCalculationResource from '../../Request/ProductCalculationResource'
import * as MessageResource from '../../Request/MessageResource'
import {productCalculationDetailsContentDecoder} from '../ProductCalculationDetails/ProductCalculationDetailsState'
import {ProductCalculationImportAction} from './ProductCalculationImportActions'
import {
    ProductCalculationImportFeedback,
    ProductCalculationImportRequestIdentifier
} from './ProductCalculationImportFeedback'
import {
    createCreateMessageModalInput,
    createEmptyForm,
    CreateMessageModal,
    CreateMessageModalInput,
    createProductCalculationAttribute,
    ProductCalculationAttribute,
    ProductCalculationImportFormular,
    ProductCalculationImportState,
    ProductCalculationPriceRow
} from './ProductCalculationImportState'
import * as PriceListCreate from '../../Entities/PriceListCreate'
import * as ProductCalculationCreate from '../../Entities/ProductCalculationCreate'

export function activate(session: Session): HttpRequest {
    return ProductCalculationResource.prepareImportData(session.manufacturer.id)
}

export function initialDecoder(): Decoder<{
    availableShippingServices: ProductShippingService[],
    availableCustomers: Customer[]
}> {
    return productCalculationImportBaseDataDecoder()
}

export function init(
    {availableShippingServices, availableCustomers}: ProductCalculationImportBaseData,
): ProductCalculationImportState {
    return {
        csvUploadStatus: {type: 'None'},
        availableShippingServices,
        availableCustomers,
        showFormular: false,
        form: createEmptyForm(),
        createMessageModal: {
            open: false,
            key: '',
            saving: null,
            type: createCreateMessageModalInput(),
            format: createCreateMessageModalInput(),
            coverage: createCreateMessageModalInput(),
            print: createCreateMessageModalInput(),
            paper: createCreateMessageModalInput(),
            processing: createCreateMessageModalInput(),
        },
        attemptedShowFormular: false,
        attemptingToSave: false,
    }
}

type ProductCalculationImportLoop =
    FeedbackLoop<ProductCalculationImportState, ProductCalculationImportFeedback>

export function reduce(
    session: Session,
    state: ProductCalculationImportState,
    action: ProductCalculationImportAction
): ProductCalculationImportLoop {
    switch (action.type) {
        case 'MenuButtonClicked':
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        case 'NoOp':
            return {
                state,
                feedback: []
            }
        case 'AddToSelectedCustomers':
            const customerToAdd: Customer[] = R.filter(
                (customer: Customer) => {
                    return R.reduce(
                        (acc: boolean, elem: Customer) => {
                            if (!acc) {
                                return elem.id === customer.id
                            }
                            return acc
                        },
                        false,
                        action.values
                    )
                },
                state.availableCustomers
            )
            return {
                state: update({
                        form: update({
                                selectedCustomers: R.concat(
                                    state.form.selectedCustomers,
                                    customerToAdd)
                            },
                            state.form)
                    },
                    state),
                feedback: []
            }
        case 'RemoveFromSelectedCustomers':
            const resultingCustomer: Customer[] = R.filter(
                (customer: Customer) => {
                    return R.reduce(
                        (acc: boolean, elem: Customer) => {
                            if (elem.id === customer.id) {
                                return false
                            }
                            return acc
                        },
                        true,
                        action.values
                    )
                },
                state.form.selectedCustomers
            )
            return {
                state: update({
                        form: update({
                                selectedCustomers: resultingCustomer
                            },
                            state.form)
                    },
                    state),
                feedback: []
            }
        case 'ManuelCreation':
            if (state.form.selectedCustomers.length > 0) {
                return {
                    state: update(
                        {
                            showFormular: true,
                            form: update({
                                    selectedCustomers: state.form.selectedCustomers,
                                    selectedShippingServiceId: state.form.selectedShippingServiceId,
                                    selectedShippingServiceValidation:
                                    state.form.selectedShippingServiceValidation
                                },
                                createEmptyForm()),
                            attemptedShowFormular: true,
                        },
                        state),
                    feedback: []
                }
            }
            return {
                state: update({attemptedShowFormular: true}, state),
                feedback: []
            }
        case 'CSVProductCalculationUpload':
            if (state.form.selectedCustomers.length <= 0) {
                return {
                    state: update({attemptedShowFormular: true}, state),
                    feedback: []
                }
            }

            const csvData = CSV.parse(action.content, ';', '"')
            try {
                const attributeImportForm: ProductCalculationImportFormular =
                    R.reduce(readAttributesFromCsv, createEmptyForm(), csvData)

                const attributePriceImportForm: ProductCalculationImportFormular =
                    readPriceDefinitionsFromCsv(attributeImportForm, csvData)

                const finalImportForm: ProductCalculationImportFormular =
                    update({
                            selectedCustomers: state.form.selectedCustomers,
                            selectedShippingServiceId: state.form.selectedShippingServiceId,
                            selectedShippingServiceValidation:
                            state.form.selectedShippingServiceValidation
                        },
                        attributePriceImportForm)

                return {
                    state: update({
                            attemptedShowFormular: true,
                            showFormular: true,
                            csvUploadStatus: {
                                type: 'Success',
                                value: csvData,
                            },
                            form: finalImportForm
                        },
                        state),
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {
                            type: 'ProductCalculationMessageSuggestionMultiListIdentifier',
                        },
                        httpRequest:
                            ProductCalculationResource.listMultiMessageSuggestions(
                                session.manufacturer.id,
                                attributeImportForm,
                            ),
                    }]
                }
            } catch (e) {
                return {
                    state: update(
                        {
                            attemptedShowFormular: true,
                            csvUploadStatus: {
                                type: 'Error',
                                reason: 'Die Formatierung der hochgeladenen Daten wird nicht unterstützt.'
                            }
                        }
                        , state),
                    feedback: []
                }
            }
        case 'ProductCalculationMessageSuggestionMultiListResponse':
            const messagesListResponse =
                decodeHttpResponse(
                    object({
                        type: field('type', array(messageWithTranslationDecoder())),
                        format: field('format', array(messageWithTranslationDecoder())),
                        coverage: field('coverage', array(messageWithTranslationDecoder())),
                        print: field('print', array(messageWithTranslationDecoder())),
                        paper: field('paper', array(messageWithTranslationDecoder())),
                        processing: field('processing', array(messageWithTranslationDecoder())),
                    }),
                    action.result)
            if (messagesListResponse.type === 'Ok') {

                const proceed: string[] = ['type', 'format', 'coverage', 'print', 'paper', 'processing']
                return {
                    state: R.reduce(
                        (
                            acc: ProductCalculationImportState,
                            currProceed: string
                        ) => {
                            return applyNewSuggestions(
                                changeSuggestedValueField(
                                    acc,
                                    currProceed,
                                    findSuggestionId(
                                        messagesListResponse.value[currProceed],
                                        acc.form,
                                        currProceed
                                    )
                                ),
                                currProceed,
                                messagesListResponse.value[currProceed]
                            )
                        },
                        state,
                        proceed
                    ),
                    feedback: []
                }
            }
            return {
                state,
                feedback: []
            }
        case 'MinQuantityChanged': {
            const newPriceList: ProductCalculationPriceRow[] = indexedMap(
                (index, price: ProductCalculationPriceRow) => {
                    return (index === action.index) ? update({
                            minQuantity: action.value,
                            minQuantityValidation: validateInputField(
                                getQuantityValidator(state.form.priceList),
                                state.form.submissionAttempted,
                                action.value
                            )
                        },
                        price)
                        : price
                },
                state.form.priceList
            )
            return {
                state: update(
                    {
                        form: update({
                                priceList: R.map(
                                    (price: ProductCalculationPriceRow) => {
                                        return update({
                                                minQuantityValidation: validateInputField(
                                                    getQuantityValidator(state.form.priceList),
                                                    state.form.submissionAttempted,
                                                    action.value
                                                )
                                            },
                                            price)
                                    },
                                    newPriceList
                                )
                            },
                            state.form)
                    },
                    state),
                feedback: []
            }
        }
        case 'PriceChanged':
            return {
                state: update(
                    {
                        form: update({
                                priceList: indexedMap(
                                    (index, price: ProductCalculationPriceRow) => {
                                        return (index === action.index) ? update({
                                                price: action.value,
                                                priceValidation: validateInputField(
                                                    priceValidator,
                                                    state.form.submissionAttempted,
                                                    action.value
                                                )
                                            },
                                            price)
                                            : price
                                    },
                                    state.form.priceList
                                )
                            },
                            state.form)
                    },
                    state),
                feedback: []
            }
        case 'AddPriceDefinitionRow':
            return {
                state: update(
                    {
                        form: update({
                                priceList: R.append(
                                    {
                                        minQuantity: '',
                                        minQuantityValidation: null,
                                        price: '',
                                        priceValidation: null
                                    },
                                    state.form.priceList
                                )
                            },
                            state.form
                        )
                    },
                    state),
                feedback: []
            }
        case 'RemovePriceDefinitionRow': {
            const newPriceList =
                indexedFilter(
                    index => index !== action.index,
                    state.form.priceList,
                )
            return {
                state:
                    update(
                        {form: update({priceList: newPriceList}, state.form)},
                        state
                    ),
                feedback: []
            }
        }
        case 'OpenMessageModal':
            const newMessageModal = {
                open: true,
                key: '',
                saving: null,
                type: decideIfCreationIsNeeded(state.form, 'type'),
                format: decideIfCreationIsNeeded(state.form, 'format'),
                coverage: decideIfCreationIsNeeded(state.form, 'coverage'),
                print: decideIfCreationIsNeeded(state.form, 'print'),
                paper: decideIfCreationIsNeeded(state.form, 'paper'),
                processing: decideIfCreationIsNeeded(state.form, 'processing'),
            }
            return {
                state: update(
                    {
                        createMessageModal: update({
                                key: getModalFirstKey(newMessageModal)
                            },
                            newMessageModal)
                    },
                    state),
                feedback: []
            }
        case 'CloseMessageModal':
            return {
                state: update(
                    {
                        createMessageModal: update({
                                open: false
                            },
                            state.createMessageModal)
                    },
                    state),
                feedback: []
            }
        case 'SaveMessageKey':
            let newCreateMessageModal = validateMessageKeyModal(state.createMessageModal, action.key)
            if (getMessageKeyIsValid(newCreateMessageModal, action.key)) {
                return {
                    state: update({
                        createMessageModal: update({
                            saving: action.key
                        }, newCreateMessageModal)
                    }, state),
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {
                            type: 'SaveNewMessageImportIdentifier',
                            key: action.key
                        },
                        httpRequest:
                            MessageResource.createWithTranslation(
                                state.createMessageModal[action.key].text,
                                state.createMessageModal[action.key].translation,
                            ),
                    }]
                }
            }
            return {
                state: update({
                    createMessageModal: newCreateMessageModal
                }, state),
                feedback: []
            }

        case 'SaveNewMessageImportResponse':
            const newCreatedMessage =
                decodeHttpResponse(
                    messageWithTranslationDecoder(),
                    action.result)
            if (newCreatedMessage.type !== 'Ok') {
                // Error Handling
                return {
                    state: update({
                            createMessageModal: update({
                                    saving: null
                                },
                                state.createMessageModal)
                        },
                        state),
                    feedback: []
                }
            }
            const updatedCreateMessageModal = update({
                    saving: null,
                    [action.key]: update({
                            needCreation: false,
                        },
                        state.createMessageModal[action.key])
                },
                state.createMessageModal)
            return {
                state: update({
                        createMessageModal: update({
                                open: getMessageModalKeepsOpen(updatedCreateMessageModal)
                            },
                            updatedCreateMessageModal),
                        form: update({
                                [action.key]: update({
                                        input: newCreatedMessage.value.translation,
                                        value: newCreatedMessage.value.id,
                                        wasRecentlyCreated: true
                                    },
                                    state.form[action.key])
                            },
                            state.form)
                    },
                    state),
                feedback: []
            }
        case 'NewMessageInputChange':
            return {
                state: update({
                        createMessageModal: updateCreateMessageModal(
                            state.createMessageModal,
                            action.key,
                            action.language,
                            action.value
                        )
                    },
                    state),
                feedback: []
            }
        case 'ChangeKeyedInputField':
            return {
                state: changeSuggestedInputField(state, action.key, action.value),
                feedback: []
            }
        case 'ChangeSelectedShippingService':
            return {
                state: update({
                        form: update({
                                selectedShippingServiceId: action.value,
                                selectedShippingServiceValidation: validateInputField(
                                    mandatoryNullableValidator(),
                                    state.form.submissionAttempted,
                                    action.value
                                )
                            },
                            state.form)
                    },
                    state),
                feedback: []
            }
        case 'FetchProductCalculationMessageSuggestions':
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {
                        type: 'ProductCalculationMessageSuggestionList',
                        key: action.key
                    },
                    httpRequest:
                        ProductCalculationResource.listMessageSuggestions(
                            session.manufacturer.id,
                            action.key,
                            action.value,
                        ),
                }]
            }
        case 'ClearProductCalculationMessageSuggestions':
            return {
                state: applyNewSuggestions(state, action.key, []),
                feedback: []
            }
        case 'ProductCalculationMessageSearchChanged':
            return {
                state: changeSuggestedInputField(state, action.key, action.value),
                feedback: []
            }
        case 'ProductCalculationMessageChanged':
            return {
                state: changeSuggestedValueField(state, action.key, action.value.id),
                feedback: []
            }
        case 'ProductCalculationMessageSuggestionResponse':
            const messages =
                decodeHttpResponse(
                    array(messageWithTranslationDecoder()),
                    action.result)
            const match = (messages.type === 'Ok') ? findSuggestionId(messages.value, state.form, action.key) : 0
            return {
                state: applyNewSuggestions(
                    changeSuggestedValueField(state, action.key, match),
                    action.key,
                    (messages.type === 'Ok') ? messages.value : []
                ),
                feedback: []
            }
        case 'SubmitProductCalculationImportFormular':
            let productShippingServiceValidation = mandatoryNullableValidator()(state.form.selectedShippingServiceId)

            let weightValidation = weightValidator(state.form.weight)

            let rowValidation = validateRows<PriceListCreate.M, ProductCalculationPriceRow>(
                (row: ProductCalculationPriceRow) => {
                    const validationResult = {
                        minQuantity: getQuantityValidator(state.form.priceList)(row.minQuantity),
                        price: priceValidator(row.price)
                    }
                    if (validationResult.minQuantity.type === 'Ok' &&
                        validationResult.price.type === 'Ok'
                    ) {
                        return {
                            data: {
                                minQuantity: validationResult.minQuantity.value,
                                price: validationResult.price.value,
                            },
                            state: row,
                        }
                    } else {
                        return {
                            data: null,
                            state: update(
                                {
                                    minQuantityValidation: Result.errorToNullable(validationResult.minQuantity),
                                    priceValidation: Result.errorToNullable(validationResult.price),
                                },
                                row
                            )
                        }
                    }
                },
                state.form.priceList
            )
            if (
                productShippingServiceValidation.type === 'Ok' &&
                weightValidation.type === 'Ok' &&
                (state.form.type.input !== '' && state.form.type.value > 0) &&
                (state.form.format.input !== '' && state.form.format.value > 0) &&
                (state.form.coverage.input !== '' && state.form.coverage.value > 0) &&
                (state.form.print.input !== '' && state.form.print.value > 0) &&
                (state.form.paper.input !== '' && state.form.paper.value > 0) &&
                (state.form.processing.input !== '' && state.form.processing.value > 0) &&
                (rowValidation.data !== null)
            ) {
                let createObject: ProductCalculationCreate.M = {
                    typeMessageId: state.form.type.value,
                    formatMessageId: state.form.format.value,
                    coverageMessageId: state.form.coverage.value,
                    printMessageId: state.form.print.value,
                    paperMessageId: state.form.paper.value,
                    processingMessageId: state.form.processing.value,
                    productShippingServiceId: productShippingServiceValidation.value,
                    weight: weightValidation.value,
                    manufacturerId: session.manufacturer.id,
                    priceList: rowValidation.data,
                    customer: state.form.selectedCustomers
                }
                return {
                    state: update({
                            form: update({
                                    submissionAttempted: true,
                                    weightValidation: null,
                                    selectedShippingServiceValidation: null,
                                    priceList: rowValidation.rows
                                },
                                state.form),
                            attemptingToSave: true
                        },
                        state),
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {
                            type: 'CreateProductCalculationIdentifier'
                        },
                        httpRequest:
                            ProductCalculationResource.create(createObject),
                    }]
                }
            }
            return {
                state: update({
                        form: update({
                                submissionAttempted: true,
                                weightValidation: Result.errorToNullable(weightValidation),
                                selectedShippingServiceValidation:
                                    Result.errorToNullable(productShippingServiceValidation),
                                priceList: rowValidation.rows
                            },
                            state.form)
                    },
                    state),
                feedback: []
            }

        case 'CreateProductCalculationResponse':
            const saveResult =
                decodeHttpResponse(
                    productCalculationDetailsContentDecoder(),
                    action.result)
            if (saveResult.type === 'Ok') {
                return {
                    state: update({attemptingToSave: false}, state),
                    feedback: [{
                        type: 'RunEffect',
                        effect:
                            navigate<ProductCalculationImportAction>(
                                url({type: 'ProductCalculationOverview'}),
                                {type: 'NoOp'}
                            )
                    }]
                }
            }
            return {
                state: update({attemptingToSave: false}, state),
                feedback: []
            }

    }
}

export function httpResponseAction(
    identifier: ProductCalculationImportRequestIdentifier,
    result: Result.Result<HttpError, string>
): ProductCalculationImportAction {
    switch (identifier.type) {
        case 'ProductCalculationMessageSuggestionList':
            return {
                type: 'ProductCalculationMessageSuggestionResponse',
                result: result,
                key: identifier.key
            }
        case 'SaveNewMessageImportIdentifier':
            return {
                type: 'SaveNewMessageImportResponse',
                result: result,
                key: identifier.key
            }
        case 'CreateProductCalculationIdentifier':
            return {
                type: 'CreateProductCalculationResponse',
                result: result
            }
        case 'ProductCalculationMessageSuggestionMultiListIdentifier':
            return {
                type: 'ProductCalculationMessageSuggestionMultiListResponse',
                result: result
            }
    }
}

function changeSuggestedValueField(
    state: ProductCalculationImportState,
    key: string,
    value: number
): ProductCalculationImportState {
    let formChange = {}
    switch (key) {
        case 'type':
            formChange = {type: updateValue(state.form.type, value)}
            break
        case 'format':
            formChange = {format: updateValue(state.form.format, value)}
            break
        case 'coverage':
            formChange = {coverage: updateValue(state.form.coverage, value)}
            break
        case 'print':
            formChange = {print: updateValue(state.form.print, value)}
            break
        case 'paper':
            formChange = {paper: updateValue(state.form.paper, value)}
            break
        case 'processing':
            formChange = {processing: updateValue(state.form.processing, value)}
            break
    }
    return update(
        {
            form: update(
                formChange,
                state.form
            )
        },
        state
    )
}

function changeSuggestedInputField(
    state: ProductCalculationImportState,
    key: string,
    value: string
): ProductCalculationImportState {
    let formChange = {}
    switch (key) {
        case 'type':
            formChange = {type: updateInput(state.form.type, value)}
            break
        case 'format':
            formChange = {format: updateInput(state.form.format, value)}
            break
        case 'coverage':
            formChange = {coverage: updateInput(state.form.coverage, value)}
            break
        case 'print':
            formChange = {print: updateInput(state.form.print, value)}
            break
        case 'paper':
            formChange = {paper: updateInput(state.form.paper, value)}
            break
        case 'processing':
            formChange = {processing: updateInput(state.form.processing, value)}
            break
        case 'weight':
            formChange = {
                weight: value,
                weightValidation: weightValidator(value)
            }
            break
    }
    return update(
        {
            form: update(
                formChange,
                state.form
            )
        },
        state
    )
}

function applyNewSuggestions(
    state: ProductCalculationImportState,
    key: string,
    suggestions: MessageWithTranslation[]
): ProductCalculationImportState {
    let formChange = {}
    switch (key) {
        case 'type':
            formChange = {type: updateSuggestions(state.form.type, suggestions)}
            break
        case 'format':
            formChange = {format: updateSuggestions(state.form.format, suggestions)}
            break
        case 'coverage':
            formChange = {coverage: updateSuggestions(state.form.coverage, suggestions)}
            break
        case 'print':
            formChange = {print: updateSuggestions(state.form.print, suggestions)}
            break
        case 'paper':
            formChange = {paper: updateSuggestions(state.form.paper, suggestions)}
            break
        case 'processing':
            formChange = {processing: updateSuggestions(state.form.processing, suggestions)}
            break
    }
    return update(
        {
            form: update(
                formChange,
                state.form
            )
        },
        state
    )
}

function findSuggestionId(
    list: MessageWithTranslation[],
    form: ProductCalculationImportFormular,
    key: string
) {
    let formInputBox = getFormInputBoxByKey(form, key)
    let match = R.find(
        (suggestion: MessageWithTranslation) => {
            return suggestion.text === formInputBox.input || suggestion.translation === formInputBox.input
        },
        list
    )
    return (match === undefined) ? 0 : match.id
}

function getFormInputBoxByKey(
    form: ProductCalculationImportFormular,
    key: string
) {
    switch (key) {
        case 'type':
            return form.type
        case 'format':
            return form.format
        case 'coverage':
            return form.coverage
        case 'print':
            return form.print
        case 'paper':
            return form.paper
        case 'processing':
            return form.processing
    }
    return form.type
}

function updateInput(
    attribute: ProductCalculationAttribute,
    value: string
): ProductCalculationAttribute {
    return update({
            input: value
        },
        attribute)
}

function updateSuggestions(
    attribute: ProductCalculationAttribute,
    suggestions: MessageWithTranslation[]
): ProductCalculationAttribute {
    return update({
            suggestions: suggestions
        },
        attribute)
}

function updateValue(
    attribute: ProductCalculationAttribute,
    value: number
): ProductCalculationAttribute {
    return update({
            value: value
        },
        attribute)
}

function decideIfCreationIsNeeded(
    form: ProductCalculationImportFormular,
    type: string
): CreateMessageModalInput {
    switch (type) {
        case 'type':
            return {
                needCreation: (form.type.input === '' || form.type.value === 0),
                submissionAttempted: false,
                text: '',
                textValidation: null,
                translation: form.type.input,
                translationValidation: null
            }
        case 'format':
            return {
                needCreation: (form.format.input === '' || form.format.value === 0),
                submissionAttempted: false,
                text: '',
                textValidation: null,
                translation: form.format.input,
                translationValidation: null
            }
        case 'coverage':
            return {
                needCreation: (form.coverage.input === '' || form.coverage.value === 0),
                submissionAttempted: false,
                text: '',
                textValidation: null,
                translation: form.coverage.input,
                translationValidation: null
            }
        case 'print':
            return {
                needCreation: (form.print.input === '' || form.print.value === 0),
                submissionAttempted: false,
                text: '',
                textValidation: null,
                translation: form.print.input,
                translationValidation: null
            }
        case 'paper':
            return {
                needCreation: (form.paper.input === '' || form.paper.value === 0),
                submissionAttempted: false,
                text: '',
                textValidation: null,
                translation: form.paper.input,
                translationValidation: null
            }
        case 'processing':
            return {
                needCreation: (form.processing.input === '' || form.processing.value === 0),
                submissionAttempted: false,
                text: '',
                textValidation: null,
                translation: form.processing.input,
                translationValidation: null
            }
    }
    return {
        needCreation: false,
        submissionAttempted: false,
        text: '',
        textValidation: null,
        translation: '',
        translationValidation: null
    }
}

function getModalFirstKey(createMessageModal: CreateMessageModal): string {
    if (createMessageModal.type.needCreation === true) {
        return 'type'
    } else if (createMessageModal.format.needCreation === true) {
        return 'format'
    } else if (createMessageModal.coverage.needCreation === true) {
        return 'coverage'
    } else if (createMessageModal.print.needCreation === true) {
        return 'print'
    } else if (createMessageModal.paper.needCreation === true) {
        return 'paper'
    } else if (createMessageModal.processing.needCreation === true) {
        return 'processing'
    }
    return ''
}

function updateCreateMessageModal(
    current: CreateMessageModal,
    key: string,
    language: string,
    value: string
): CreateMessageModal {
    switch (key) {
        case 'type':
            return update({type: updateCreateMessageModalInput(current.type, language, value)}, current)
        case 'format':
            return update({format: updateCreateMessageModalInput(current.format, language, value)}, current)
        case 'coverage':
            return update({coverage: updateCreateMessageModalInput(current.coverage, language, value)}, current)
        case 'print':
            return update({print: updateCreateMessageModalInput(current.print, language, value)}, current)
        case 'paper':
            return update({paper: updateCreateMessageModalInput(current.paper, language, value)}, current)
        case 'processing':
            return update(
                {processing: updateCreateMessageModalInput(current.processing, language, value)},
                current
            )
    }
    return current
}

function updateCreateMessageModalInput(
    current: CreateMessageModalInput,
    language: string,
    value: string
): CreateMessageModalInput {
    let updatedCreateMessageModal = (language === 'DE')
        ? update(
            {
                translation: value
            },
            current
        )
        : update(
            {
                text: value
            },
            current
        )
    if (updatedCreateMessageModal.submissionAttempted) {
        updatedCreateMessageModal = update({
                textValidation: validateInputField(
                    mandatoryStringValidator(),
                    updatedCreateMessageModal.submissionAttempted,
                    updatedCreateMessageModal.text
                ),
                translationValidation: validateInputField(
                    mandatoryStringValidator(),
                    updatedCreateMessageModal.submissionAttempted,
                    updatedCreateMessageModal.translation
                ),
            },
            updatedCreateMessageModal)
    }
    return updatedCreateMessageModal

}

function getMessageKeyIsValid(
    createMessageModal: CreateMessageModal,
    key: string
) {
    return (
        createMessageModal[key].translationValidation === null
        && createMessageModal[key].textValidation === null
    )
}

function validateMessageKeyModal(
    createMessageModal: CreateMessageModal,
    key: string
): CreateMessageModal {
    return update({
            [key]: update({
                    textValidation: validateInputField(
                        mandatoryStringValidator(),
                        true,
                        createMessageModal[key].text
                    ),
                    translationValidation: validateInputField(
                        mandatoryStringValidator(),
                        true,
                        createMessageModal[key].translation
                    ),
                },
                createMessageModal[key])
        },
        createMessageModal)
}

function getMessageModalKeepsOpen(createMessageModal: CreateMessageModal) {
    if (createMessageModal.type.needCreation) {
        return true
    }
    if (createMessageModal.format.needCreation) {
        return true
    }
    if (createMessageModal.coverage.needCreation) {
        return true
    }
    if (createMessageModal.print.needCreation) {
        return true
    }
    if (createMessageModal.paper.needCreation) {
        return true
    }
    if (createMessageModal.processing.needCreation) {
        return true
    }
    return false
}

function getQuantityValidator(arrayList: ProductCalculationPriceRow[]) {
    return Result.compose(
        Result.compose(
            stringToIntValidator(),
            mandatoryStringValidator()
        ),
        arrayHasUniqueStringKeyValidator(arrayList, (elem: ProductCalculationPriceRow) => elem.minQuantity)
    )
}

const priceValidator = Result.compose(
    stringToFixedPointNumberValidator(3),
    mandatoryStringValidator()
)
const weightValidator = Result.compose(
    stringToIntValidator(),
    mandatoryStringValidator(),
)

function readAttributesFromCsv(
    importFormular: ProductCalculationImportFormular,
    csvLine: string[]
): ProductCalculationImportFormular {
    switch (csvLine[0]) {
        case 'TYP':
        case 'TYPE':
            return update({
                    type: createProductCalculationAttribute(csvLine[1])
                },
                importFormular)
        case 'FORMAT':
            return update({
                    format: createProductCalculationAttribute(csvLine[1])
                },
                importFormular)
        case 'UMFANG':
        case 'COVERAGE':
            return update({
                    coverage: createProductCalculationAttribute(csvLine[1])
                },
                importFormular)
        case 'PRINT':
        case 'DRUCK':
            return update({
                    print: createProductCalculationAttribute(csvLine[1])
                },
                importFormular)
        case 'PAPER':
        case 'PAPIER':
            return update({
                    paper: createProductCalculationAttribute(csvLine[1])
                },
                importFormular)
        case 'PROCESSING':
        case 'VERARBEITUNG':
            return update({
                    processing: createProductCalculationAttribute(csvLine[1])
                },
                importFormular)
        case 'GEWICHT (Gramm)':
        case 'GEWICHT (g)':
        case 'GEWICHT':
        case 'WEIGHT':
            return update({
                    weight: csvLine[1]
                },
                importFormular)
        default:
            return importFormular
    }
}

function readPriceDefinitionsFromCsv(
    attributeImportForm: ProductCalculationImportFormular,
    content: string[][]
): ProductCalculationImportFormular {
    let i = 0
    while (content[i] !== undefined && content[i][0] !== 'MINDESTANZAHL' && content[i][0] !== 'MINQUANTITY') {
        i++
    }
    let start = i
    i++
    while (content[i] !== undefined && !isNaN(Number(content[i][0]))) {
        i++
    }
    let end = i - 1
    let result: ProductCalculationPriceRow[] = []
    for (let run = start + 1; run <= end; run++) {
        result.push({
            minQuantity: content[run][0],
            minQuantityValidation: null,
            price: content[run][1],
            priceValidation: null
        })
    }

    return update({
            priceList: result
        },
        attributeImportForm)
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/ProductCalculationImport/ProductCalculationImportReducer.ts